import { DomainError } from './errors';
import type {
  FoodContainer,
  NumberAssignmentKind,
  IsoDateTime,
} from './model';

export const ACTIVE_NUMBER_STATUSES = new Set<FoodContainer['status']>([
  'draft',
  'present',
  'partial',
]);

export const VISIBLE_NUMBER_QUARANTINE = {
  days: 30,
  version: 'visible-number-quarantine-v1',
} as const;

export interface VisibleNumberRange {
  start: number;
  end: number | null;
}

export interface VisibleNumberAssignment {
  serialNumber: number;
  assignedAt: IsoDateTime;
  kind: NumberAssignmentKind;
}

export const LOCAL_VISIBLE_NUMBER_RANGE: VisibleNumberRange = {
  start: 1,
  end: null,
};

export type NumberedContainer = FoodContainer & {
  trackingMode: 'visible-number';
  serialNumber: number;
  numberAssignedAt: IsoDateTime;
  numberAssignmentKind: NumberAssignmentKind;
  numberPolicyVersion: string;
};

export const hasVisibleNumber = (container: FoodContainer): container is NumberedContainer =>
  container.trackingMode === 'visible-number' &&
  container.serialNumber != null &&
  container.numberAssignedAt != null &&
  container.numberAssignmentKind != null &&
  container.numberPolicyVersion != null;

export const holdsVisibleNumber = (container: FoodContainer): container is NumberedContainer =>
  hasVisibleNumber(container) && ACTIVE_NUMBER_STATUSES.has(container.status);

export const calculateVisibleNumberReleaseAt = (quarantinedAt: IsoDateTime): IsoDateTime => {
  const date = new Date(quarantinedAt);
  if (Number.isNaN(date.getTime())) {
    throw new DomainError('La date de mise en quarantaine est invalide.', 'INVALID_STATE');
  }
  date.setUTCDate(date.getUTCDate() + VISIBLE_NUMBER_QUARANTINE.days);
  return date.toISOString();
};

export const reservesVisibleNumber = (
  container: FoodContainer,
  at: IsoDateTime,
): boolean => {
  if (!hasVisibleNumber(container)) return false;
  return (
    ACTIVE_NUMBER_STATUSES.has(container.status) ||
    container.numberReleasedAt == null ||
    container.numberReleasedAt > at
  );
};

export const allocateSmallestVisibleNumber = (
  containers: readonly FoodContainer[],
  at: IsoDateTime,
  range: VisibleNumberRange = LOCAL_VISIBLE_NUMBER_RANGE,
): number => {
  if (!Number.isInteger(range.start) || range.start <= 0) {
    throw new DomainError('La plage de numéros visibles est invalide.', 'INVALID_STATE');
  }
  const used = new Set(
    containers
      .filter((container) => reservesVisibleNumber(container, at))
      .flatMap((container) => (container.serialNumber == null ? [] : [container.serialNumber]))
      .filter((serialNumber) => serialNumber >= range.start),
  );
  let candidate = range.start;
  while (used.has(candidate) && (range.end == null || candidate <= range.end)) {
    candidate += 1;
  }
  if (range.end != null && candidate > range.end) {
    throw new DomainError('Aucun numéro visible n’est libre dans la plage autorisée.', 'LIMIT_REACHED');
  }
  return candidate;
};

export const selectRestorationAssignment = (
  container: FoodContainer,
  allContainers: readonly FoodContainer[],
  assignedAt: IsoDateTime,
): VisibleNumberAssignment => {
  if (!hasVisibleNumber(container)) {
    throw new DomainError('Cet aliment ne possède aucun numéro visible à restaurer.', 'INVALID_STATE');
  }
  const originalNumberIsHeld = allContainers.some(
    (candidate) =>
      candidate.id !== container.id &&
      reservesVisibleNumber(candidate, assignedAt) &&
      candidate.serialNumber === container.serialNumber,
  );
  if (!originalNumberIsHeld) {
    return {
      serialNumber: container.serialNumber,
      assignedAt,
      kind: 'restored',
    };
  }
  return {
    serialNumber: allocateSmallestVisibleNumber(allContainers, assignedAt),
    assignedAt,
    kind: 'reassigned',
  };
};
